import { APIProvider, Map, AdvancedMarker, useMap, MapControl, ControlPosition } from '@vis.gl/react-google-maps';
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getHealthStatusColor } from '../../energy/utils';
import { MapMarker, MapDisplayType } from '../types';
import { HealthStatus, StatusFilter } from '../../../types';
import { isUnderConstruction } from '../../../utils';
import { calculateBounds } from '../utils';
import { MapIcon, ArrowPathRoundedSquareIcon, GlobeAsiaAustraliaIcon, SunIcon, Battery100Icon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import StatusFilterDropdown from '../../../components/StatusFilterDropdown';

const victronLogo = new URL('../../../images/victron.svg', import.meta.url).href;
const smaLogo = new URL('../../../images/sma.svg', import.meta.url).href;

const MARKER_SIZE = 10;

/** Z-index layer per health status – higher values render on top. */
const STATUS_Z_INDEX: Record<HealthStatus, number> = {
    'normal': 40,
    'system-fault': 40,
    'no-data': 30,
    'manual': 20,
    'under-construction': 10,
};

const getStatusZIndex = (status?: HealthStatus): number =>
    status ? (STATUS_Z_INDEX[status] ?? 0) : 0;

interface MapComponentProps {
    isLoading: boolean;
    markers: MapMarker[];
    onMarkerClick?: (id: number) => void;
    onMarkerHover?: (id: number | null) => void;
    hoveredMarkerId?: number | null;
    enableControls?: boolean;
    statusFilter?: StatusFilter;
    onStatusFilterChange?: Dispatch<SetStateAction<StatusFilter>>;
    showNavigation?: boolean;
    currentSiteId?: number | null;
    onNavigateToSite?: (siteId: number) => void;
    navigationMarkers?: MapMarker[];
    showFilter?: boolean;
}

const getMarkerCircle = (marker: MapMarker, hoveredMarkerId: number | null) => {
    const statusColor = getHealthStatusColor(marker.health_status);
    const fill = statusColor;
    const fillOpacity = hoveredMarkerId === marker.id ? 1 : 0.6;
    const stroke = statusColor;
    const strokeWidth = 1;
    const center = MARKER_SIZE / 2;
    const radius = center - strokeWidth / 2;
    return <circle r={radius} cx={center} cy={center} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={strokeWidth} />;
};

const getManufacturerLogo = (manufacturer?: string): string | null => {
    if (!manufacturer) return null;

    const manufacturerLower = manufacturer.toLowerCase();
    if (manufacturerLower.includes('victron')) {
        return victronLogo;
    } else if (manufacturerLower.includes('sma')) {
        return smaLogo;
    }
    return null;
};

const getHealthStatusDisplay = (status?: HealthStatus) => {
    switch (status) {
        case 'manual':
            return { text: 'Manual', color: 'bg-blue-300', reason: 'Manual operation' };
        case 'under-construction':
            return { text: 'Under construction', color: 'bg-white text-gray-800', reason: 'System under construction' };
        case 'normal':
            return { text: 'Normal', color: 'bg-success', reason: 'Normal operation' };
        case 'system-fault':
            return { text: 'System fault', color: 'bg-error', reason: 'System fault' };
        case 'no-data':
            return { text: 'No data', color: 'bg-gray-200', reason: 'No recent data available' };
    }
};

const MarkerTooltipContent: FC<{ marker: MapMarker; healthStatus: ReturnType<typeof getHealthStatusDisplay> }> = ({ marker, healthStatus }) => (
    <div className="relative">
        <div className="flex justify-between items-center mb-1 gap-3">
            <span className="font-bold text-sm">{marker.siteName}</span>
            <div className="flex items-center gap-1">
                <div className="rounded-lg w-2.5 h-2.5" style={{ backgroundColor: getHealthStatusColor(marker.health_status) }}></div>
                <span className="whitespace-nowrap">{healthStatus?.text}</span>
            </div>
        </div>
        {marker.address && (
            <p className="text-gray-300 mb-1">{marker.address}</p>
        )}
        <div className="flex gap-2 mt-1">
            {marker.generation_kw && marker.generation_kw > 0 && (
                <div className="flex items-center gap-1 bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">
                    <SunIcon className="w-3 h-3" />
                    <span className="font-medium">{marker.generation_kw} kW</span>
                </div>
            )}
            {marker.storage_capacity_kwh && marker.storage_capacity_kwh > 0 && (
                <div className="flex items-center gap-1 bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">
                    <Battery100Icon className="w-3 h-3" />
                    <span className="font-medium">{marker.storage_capacity_kwh} kWh</span>
                </div>
            )}
        </div>
    </div>
);

const MarkerWithTooltip: FC<{
    marker: MapMarker;
    isHovered: boolean;
    hoveredMarkerId: number | null;
    popDirection: 'up' | 'down';
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onClick: () => void;
}> = ({ marker, isHovered, hoveredMarkerId, popDirection, onMouseEnter, onMouseLeave, onClick }) => {
    const healthStatus = getHealthStatusDisplay(marker.health_status);

    return (
        <AdvancedMarker
            key={`marker-${marker.id}`}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            zIndex={isHovered ? 1000 : getStatusZIndex(marker.health_status)}
        >
            <div className="relative flex flex-col items-center">
                {/* Tooltip above marker */}
                {isHovered && popDirection === 'up' && (
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-pacificgreen-800 text-white text-xs rounded-lg p-2 w-max max-w-[250px] shadow-lg">
                        <MarkerTooltipContent marker={marker} healthStatus={healthStatus} />
                        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-pacificgreen-800 rotate-45"></div>
                    </div>
                )}
                <svg width={MARKER_SIZE} height={MARKER_SIZE} viewBox={`0 0 ${MARKER_SIZE} ${MARKER_SIZE}`} xmlns="http://www.w3.org/2000/svg">
                    {getMarkerCircle(marker, hoveredMarkerId)}
                </svg>
                {/* Tooltip below marker */}
                {isHovered && popDirection === 'down' && (
                    <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gray-700 text-white text-xs rounded-lg p-2 w-max max-w-[250px] shadow-lg">
                        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45"></div>
                        <MarkerTooltipContent marker={marker} healthStatus={healthStatus} />
                    </div>
                )}
            </div>
        </AdvancedMarker>
    );
};

const MapZoomControl = ({ markers, shouldRefit }: { markers: MapMarker[], shouldRefit: number }) => {
    const map = useMap();

    useEffect(() => {
        if (window && map && markers && markers.length > 0) {
            const bounds = calculateBounds(markers);
            const latLngBounds = new window.google.maps.LatLngBounds(
                { lat: bounds.south, lng: bounds.west },
                { lat: bounds.north, lng: bounds.east }
            );
            map.fitBounds(latLngBounds);
        }
    }, [map, markers, shouldRefit]);

    return null;
};

const MapComponent: FC<MapComponentProps> = ({
    isLoading,
    markers,
    onMarkerClick,
    onMarkerHover,
    hoveredMarkerId,
    enableControls = true,
    statusFilter,
    onStatusFilterChange,
    showNavigation = false,
    currentSiteId,
    onNavigateToSite,
    navigationMarkers,
    showFilter = false
}: MapComponentProps) => {
    const bounds = calculateBounds(markers);
    const [mapDisplayType, setMapDisplayType] = useState<MapDisplayType>('roadmap');
    const [refitCounter, setRefitCounter] = useState(0);
    const [hoveredMarker, setHoveredMarker] = useState<MapMarker | null>(null);
    const [isMapHovered, setIsMapHovered] = useState(false);
    const [leftHovered, setLeftHovered] = useState(false);
    const [rightHovered, setRightHovered] = useState(false);

    // Filter out markers without valid coordinates and sort by z-index so higher-priority statuses render on top
    const filteredMarkers = useMemo(() => {
        return markers
            .filter(m => m.latitude && m.longitude)
            .sort((a, b) => getStatusZIndex(a.health_status) - getStatusZIndex(b.health_status));
    }, [markers]);

    // Count markers per health status for the legend
    const statusCounts = useMemo(() => {
        const counts: Partial<Record<HealthStatus, number>> = {};
        for (const m of filteredMarkers) {
            if (m.health_status) {
                counts[m.health_status] = (counts[m.health_status] ?? 0) + 1;
            }
        }
        return counts;
    }, [filteredMarkers]);

    const navigationList = navigationMarkers ?? filteredMarkers;
    const hasNavigation = showNavigation && onNavigateToSite && currentSiteId !== null && currentSiteId !== undefined && navigationList.length > 1;

    const handleNavigate = (direction: 'prev' | 'next') => {
        if (!hasNavigation || !onNavigateToSite) return;

        const currentIndex = navigationList.findIndex(marker => marker.id === currentSiteId);
        const safeIndex = currentIndex === -1 ? 0 : currentIndex;
        const nextIndex = direction === 'prev'
            ? (safeIndex - 1 + navigationList.length) % navigationList.length
            : (safeIndex + 1) % navigationList.length;

        onNavigateToSite(navigationList[nextIndex].id);
    };

    const handleMarkerMouseEnter = (marker: MapMarker) => {
        setHoveredMarker(marker);
        if (onMarkerHover) {
            onMarkerHover(marker.id);
        }
    };

    const handleMarkerMouseLeave = () => {
        setHoveredMarker(null);
        if (onMarkerHover) {
            onMarkerHover(null);
        }
    };

    const midLatitude = filteredMarkers.length > 0
        ? (Math.max(...filteredMarkers.map(m => m.latitude)) + Math.min(...filteredMarkers.map(m => m.latitude))) / 2
        : 0;

    return (
        <>
            <div
                className='relative w-full h-full'
                onMouseEnter={() => setIsMapHovered(true)}
                onMouseLeave={() => setIsMapHovered(false)}
            >
                <APIProvider apiKey={'AIzaSyAjSExMPaf4YI1UCsnmfBNWsZsgvz7Mb04'}>
                    {enableControls && <MapControl position={ControlPosition.TOP_RIGHT}>
                        <div className="m-2 flex gap-1 text-white">
                            <button
                                className="rounded-lg bg-white/25 hover:bg-white/50 flex justify-center items-center cursor-pointer p-0 size-8"
                                onClick={() => setRefitCounter(c => c + 1)}
                                title="Refit map to markers"
                            >
                                <svg className="h-4 w-4" fill="currentColor" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 26 26">
                                    <path fillRule='evenodd' d="M24,24v-8h2v10h-10v-2h8ZM2,24h8v2H0v-10h2v8ZM26,2v8h-2V2h-8V0h10v2ZM2,2v8H0V0h10v2H2Z" />
                                </svg>
                            </button>
                            <button
                                className="rounded-lg bg-white/25 hover:bg-white/50 flex justify-center items-center cursor-pointer p-0 size-8"
                                onClick={() => setMapDisplayType(mapDisplayType === 'hybrid' ? 'roadmap' : 'hybrid')}
                                title={mapDisplayType === 'hybrid' ? 'Show map' : 'Show satellite'}
                            >
                                {mapDisplayType === 'hybrid' ? <MapIcon className="w-5 h-5 m-0" /> : <GlobeAsiaAustraliaIcon className="w-5 h-5 m-0" />}
                            </button>
                            {showFilter && statusFilter && onStatusFilterChange && (
                                <StatusFilterDropdown
                                    statusFilter={statusFilter}
                                    onStatusFilterChange={onStatusFilterChange}
                                    variant="map"
                                />
                            )}
                        </div>
                    </MapControl>}
                    <MapZoomControl markers={filteredMarkers} shouldRefit={refitCounter} />
                    <Map
                        defaultBounds={bounds}
                        gestureHandling={'greedy'}
                        disableDefaultUI={true}
                        mapId="7b4f342f6bafeac5c4e46637"
                        mapTypeId={mapDisplayType}
                        reuseMaps={true}
                        colorScheme="DARK"
                    >
                        {filteredMarkers.map(marker => (
                            <MarkerWithTooltip
                                key={`marker-${marker.id}`}
                                marker={marker}
                                isHovered={hoveredMarker?.id === marker.id}
                                hoveredMarkerId={hoveredMarkerId || null}
                                popDirection={marker.latitude >= midLatitude ? 'down' : 'up'}
                                onMouseEnter={() => handleMarkerMouseEnter(marker)}
                                onMouseLeave={handleMarkerMouseLeave}
                                onClick={() => onMarkerClick && onMarkerClick(marker.id)}
                            />
                        ))}
                    </Map>
                </APIProvider>
                {hasNavigation && (
                    <>
                        <button
                            className={`absolute left-3 top-1/2 -translate-y-1/2 transition-all duration-200 z-10 cursor-pointer ${isMapHovered ? 'opacity-90 pointer-events-auto' : 'opacity-0 pointer-events-none'} ${leftHovered ? 'bg-white/90 hover:bg-white text-pacificblue-800 shadow-lg p-3 rounded-full' : 'bg-transparent text-white p-0 shadow-none'}`}
                            onClick={() => handleNavigate('prev')}
                            onMouseEnter={() => setLeftHovered(true)}
                            onMouseLeave={() => setLeftHovered(false)}
                            aria-label="Previous site"
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        <button
                            className={`absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-200 z-10 cursor-pointer ${isMapHovered ? 'opacity-90 pointer-events-auto' : 'opacity-0 pointer-events-none'} ${rightHovered ? 'bg-white/90 hover:bg-white text-pacificblue-800 shadow-lg p-3 rounded-full' : 'bg-transparent text-white p-0 shadow-none'}`}
                            onClick={() => handleNavigate('next')}
                            onMouseEnter={() => setRightHovered(true)}
                            onMouseLeave={() => setRightHovered(false)}
                            aria-label="Next site"
                        >
                            <ChevronRightIcon className="w-5 h-5" />
                        </button>
                    </>
                )}
                {/* Legend */}
                <div className="absolute left-2 top-2 z-10 bg-gray-700/40 backdrop-blur-sm rounded-lg px-3 py-2 text-white text-xs space-y-1">
                    {[
                        { status: 'normal' as const, label: 'Normal', tooltip: 'Data was collected within the last 24 hours', hollow: false },
                        { status: 'system-fault' as const, label: 'System fault', tooltip: 'System has reported a fault', hollow: false },
                        { status: 'no-data' as const, label: 'No data', tooltip: 'No recent data received from the site', hollow: false },
                        { status: 'manual' as const, label: 'Manual', tooltip: 'No remote monitoring capability', hollow: false },
                        { status: 'under-construction' as const, label: 'Under construction', tooltip: 'Site is currently under construction', hollow: false },
                    ].map(item => {
                        const color = getHealthStatusColor(item.status);
                        const strokeWidth = 1;
                        return (
                        <div key={item.status} className="tooltip tooltip-right block w-fit" data-tip={item.tooltip} style={{ '--tt-bg': '#374151' } as React.CSSProperties}>
                            <div className="flex items-center gap-2 cursor-help">
                                <svg width={MARKER_SIZE} height={MARKER_SIZE} viewBox={`0 0 ${MARKER_SIZE} ${MARKER_SIZE}`}>
                                    <circle
                                        cx={MARKER_SIZE / 2} cy={MARKER_SIZE / 2} r={MARKER_SIZE / 2 - strokeWidth / 2}
                                        fill={item.hollow ? 'transparent' : color}
                                        fillOpacity={item.hollow ? 0 : 0.6}
                                        stroke={color}
                                        strokeWidth={strokeWidth}
                                    />
                                </svg>
                                <span>{item.label}</span>
                                <span className="text-white/50">{statusCounts[item.status] ?? 0}</span>
                            </div>
                        </div>
                    )})}
                </div>
                {isLoading
                    ? <div className="absolute inset-0 flex items-center justify-center bg-pacificblue-900/25 z-10 gap-2">
                        <span className="loading loading-spinner loading-xl text-white"></span>
                    </div>
                    : filteredMarkers.length === 0
                        ? <div className="absolute inset-0 flex items-center justify-center bg-pacificblue-900 z-10 gap-2">
                            <span className="text-white text-center px-4">
                                {markers.length === 0
                                    ? 'No sites to display'
                                    : markers.every(m => !m.latitude || !m.longitude)
                                        ? 'No sites have coordinates to display on the map'
                                        : 'No sites match the current filter'}
                            </span>
                        </div>
                        : <></>
                }
            </div>
        </>
    );
};

export default MapComponent;
